
import { useActionState, useEffect, useState } from "react";
import { requestOtpAction, verifyLoginOtpAction, type LoginState } from "@frontend/lib/actions";
import { SubmitButton } from "@frontend/components/action-form";
import { OtpInput } from "@frontend/components/otp-input";
import { Callout, Field } from "@frontend/components/ui";
import { resolveAuthNext } from "@frontend/lib/return-to";

export function LoginForm({ next }: { next?: string }) {
  const [phone, setPhone] = useState("");
  const [sentPhone, setSentPhone] = useState<string | null>(null);
  const [devCode, setDevCode] = useState<string | null>(null);
  const [returnTo, setReturnTo] = useState(next);

  useEffect(() => {
    setReturnTo(resolveAuthNext(next));
  }, [next]);

  const [requestState, requestAction] = useActionState(
    async (_state: LoginState, formData: FormData) => {
      const result = await requestOtpAction({}, formData);
      if (result.sent && result.phone) {
        setSentPhone(result.phone);
        setDevCode(result.devCode ?? null);
      }
      return result;
    },
    {},
  );

  const [verifyState, verifyAction] = useActionState(verifyLoginOtpAction, {});

  if (sentPhone) {
    return (
      <form action={verifyAction} noValidate className="space-y-5">
        {returnTo ? <input type="hidden" name="next" value={returnTo} /> : null}
        <input type="hidden" name="phone" value={sentPhone} />

        <p className="text-sm leading-relaxed text-ink-muted">
          {devCode ? (
            <>
              Local OTP for <span className="font-medium text-ink">{sentPhone}</span>:{" "}
              <span className="font-semibold tracking-widest text-ink">{devCode}</span>
            </>
          ) : (
            <>
              We sent a 6-digit code to WhatsApp on{" "}
              <span className="font-medium text-ink">{sentPhone}</span>.
            </>
          )}
        </p>

        <OtpInput autoFocus />

        {verifyState.error ? <Callout tone="red">{verifyState.error}</Callout> : null}

        <div className="pt-1">
          <SubmitButton size="lg" full pendingLabel="Verifying…">
            Continue
          </SubmitButton>
        </div>

        <button
          type="button"
          onClick={() => {
            setSentPhone(null);
            setDevCode(null);
          }}
          className="w-full text-center text-sm text-ink-muted transition hover:text-ink"
        >
          Use a different number
        </button>
      </form>
    );
  }

  return (
    <form action={requestAction} className="space-y-5">
      <input type="hidden" name="purpose" value="login" />

      <Field label="Mobile number" hint="We'll send a one-time code on WhatsApp.">
        <div className="relative mt-1.5">
          <span className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-sm text-ink-subtle">
            +91
          </span>
          <input
            name="phone"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            required
            autoFocus
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            placeholder="90000 00000"
            className="field-input !mt-0 pl-12"
          />
        </div>
      </Field>

      {requestState.error ? <Callout tone="red">{requestState.error}</Callout> : null}

      <div className="pt-1">
        <SubmitButton size="lg" full pendingLabel="Sending…">
          Send WhatsApp code
        </SubmitButton>
      </div>
    </form>
  );
}
